import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CampaignTemplates = ({ isOpen, onClose, onSelectTemplate }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Templates', icon: 'Grid3X3' },
    { id: 'nurturing', label: 'Client Nurturing', icon: 'Heart' },
    { id: 'followup', label: 'Follow-up', icon: 'MessageCircle' },
    { id: 'seasonal', label: 'Seasonal', icon: 'Calendar' },
    { id: 'referral', label: 'Referral', icon: 'Users' }
  ];

  const templates = [
    {
      id: 1,
      name: "Birthday Celebration Campaign",
      category: "nurturing",
      description: "Automated birthday wishes with personalized messages and special offers",
      messageCount: 3,
      duration: "7 days",
      trigger: "Client Birthday",
      popularity: 95,
      preview: [
        "Happy Birthday, {{firstName}}! 🎉 Hope your special day is amazing!",
        "Hope you had a wonderful birthday celebration! As your real estate agent, I'm here if you need anything.",
        "Thank you for being such a valued client. Wishing you a fantastic year ahead!"
      ]
    },
    {
      id: 2,
      name: "Home Anniversary Sequence",
      category: "nurturing",
      description: "Celebrate home purchase anniversaries and check in on client satisfaction",
      messageCount: 4,
      duration: "14 days",
      trigger: "Home Purchase Anniversary",
      popularity: 88,
      preview: [
        "Happy {{anniversary}} anniversary in your home, {{firstName}}! Time flies!",
        "How are you enjoying your neighborhood? Any changes you\'d like to make?",
        "Your home's estimated value has {{trend}} since purchase. Let me know if you'd like details!",
        "Thanks for trusting me with your home purchase. I\'m here for any real estate needs!"
      ]
    },
    {
      id: 3,
      name: "Post-Closing Follow-up",
      category: "followup",
      description: "Comprehensive follow-up sequence after closing to ensure client satisfaction",
      messageCount: 5,
      duration: "90 days",
      trigger: "Closing Date",
      popularity: 92,
      preview: [
        "Congratulations on your new home, {{firstName}}! Welcome to {{neighborhood}}!",
        "How's the move going? Let me know if you need recommendations for local services.",
        "Hope you\'re settling in well! Here are some great local restaurants and shops.",
        "It\'s been a month since closing. How are you enjoying your new home?",
        "90 days in your new home! I\'d love to hear how everything is going."
      ]
    },
    {
      id: 4,
      name: "Seasonal Market Updates",
      category: "seasonal",
      description: "Quarterly market insights and property value updates for past clients",
      messageCount: 4,
      duration: "90 days",
      trigger: "Quarterly",
      popularity: 76,
      preview: [
        "{{firstName}}, here's your Q{{quarter}} market update for {{neighborhood}}!",
        "Spring market trends show {{trend}} in your area. Your home value: ${{value}}",
        "Summer market update: {{neighborhood}} continues to be a strong market.",
        "Year-end market summary: Your home has {{appreciation}} this year!"
      ]
    },
    {
      id: 5,
      name: "Referral Request Campaign",
      category: "referral",
      description: "Gentle referral requests for satisfied clients with incentive offers",
      messageCount: 3,
      duration: "21 days",
      trigger: "6 months post-closing",
      popularity: 84,
      preview: [
        "Hi {{firstName}}! Hope you're loving your home. Know anyone looking to buy or sell?",
        "Your referrals mean the world to me! I have a special thank-you gift for successful referrals.",
        "Thanks for being an amazing client! Remember, I'm here for all your friends' real estate needs."
      ]
    },
    {
      id: 6,
      name: "Holiday Greetings Series",
      category: "seasonal",
      description: "Warm holiday messages throughout the year to maintain client relationships",
      messageCount: 6,
      duration: "365 days",
      trigger: "Holiday Calendar",
      popularity: 71,
      preview: [
        "Wishing you and your family a wonderful New Year, {{firstName}}!",
        "Happy Valentine\'s Day! Hope you\'re loving your home sweet home! ❤️",
        "Spring has arrived! Perfect time for home maintenance. Need any contractor recommendations?",
        "Happy 4th of July! Hope you're enjoying summer in your beautiful home!",
        "Happy Thanksgiving, {{firstName}}! Grateful to have you as a client.",
        "Merry Christmas! Wishing your family joy and happiness in your home!"
      ]
    }
  ];

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates?.filter(template => template?.category === selectedCategory);

  const handleSelectTemplate = (template) => {
    onSelectTemplate(template);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-6xl bg-card border border-border rounded-lg elevation-4 animate-scale-in max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Campaign Templates</h2>
              <p className="text-sm text-muted-foreground">Choose from proven campaign templates to get started quickly</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Categories Sidebar */}
            <div className="w-64 border-r border-border p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Categories</h3>
              <div className="space-y-1">
                {categories?.map((category) => (
                  <button
                    key={category?.id}
                    onClick={() => setSelectedCategory(category?.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-smooth ${
                      selectedCategory === category?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={category?.icon} size={16} />
                    <span>{category?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Templates Grid */}
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredTemplates?.map((template) => (
                  <div key={template?.id} className="border border-border rounded-lg p-6 hover:elevation-2 transition-smooth">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-foreground">{template?.name}</h3>
                          <div className="flex items-center space-x-1 px-2 py-1 bg-success/10 text-success rounded-full text-xs font-medium">
                            <Icon name="TrendingUp" size={12} />
                            <span>{template?.popularity}%</span>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{template?.description}</p>
                        <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                          <span className="flex items-center space-x-1">
                            <Icon name="MessageSquare" size={12} />
                            <span>{template?.messageCount} messages</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Icon name="Clock" size={12} />
                            <span>{template?.duration}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Icon name="Zap" size={12} />
                            <span>{template?.trigger}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Message Preview */}
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-foreground mb-2">Message Preview</h4>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {template?.preview?.slice(0, 2)?.map((message, index) => (
                          <div key={index} className="bg-muted/50 rounded p-2">
                            <p className="text-xs text-foreground">{message}</p>
                          </div>
                        ))}
                        {template?.preview?.length > 2 && (
                          <div className="text-xs text-muted-foreground text-center py-1">
                            +{template?.preview?.length - 2} more messages
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => {/* Preview functionality */}}
                      >
                        <Icon name="Eye" size={14} />
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => handleSelectTemplate(template)}
                      >
                        <Icon name="Plus" size={14} />
                        Use Template
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {filteredTemplates?.length === 0 && (
                <div className="text-center py-12">
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No templates found</h3>
                  <p className="text-muted-foreground">Try selecting a different category or create a custom campaign.</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-border p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {filteredTemplates?.length} template{filteredTemplates?.length !== 1 ? 's' : ''} available
              </div>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignTemplates;